// GitHub repository configuration
const GITHUB_CONFIG = {
  username: 'Aruba-Creative',
  repository: 'arubawebsite-images',
  branch: 'main',
  basePath: 'images', // The images are in the /images folder
  // Add your Personal Access Token here (remove when committing to public repos)
  accessToken: process.env.REACT_APP_GITHUB_TOKEN || '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'
};

/**
 * Constructs a jsDelivr CDN URL for a file in the GitHub repository
 * @param {string} path - Path to the file within the repository
 * @returns {string} - The jsDelivr CDN URL
 */
export const getGitHubCdnUrl = (path) => {
  const { username, repository, branch, basePath } = GITHUB_CONFIG;
  // Ensure path doesn't start with a slash
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  // Construct the full path including the base path
  const fullPath = basePath ? `${basePath}/${cleanPath}` : cleanPath;
  // Return the jsDelivr CDN URL
  return `https://cdn.jsdelivr.net/gh/${username}/${repository}@${branch}/${fullPath}`;
};

/**
 * Fetches content from a GitHub repository directory
 * @param {string} path - Path within the repository to fetch content from
 * @returns {Promise<Array>} - Array of file and folder objects
 */
export const fetchGitHubContent = async (path = '') => {
  const { username, repository, branch, basePath, accessToken } = GITHUB_CONFIG;
  const apiPath = basePath ? (path ? `${basePath}/${path}` : basePath) : path;
  
  console.log(`Fetching GitHub content from path: ${apiPath}`);
  const apiUrl = `https://api.github.com/repos/${username}/${repository}/contents/${apiPath}?ref=${branch}`;
  console.log(`API URL: ${apiUrl}`);
  
  try {
    // Add authentication headers if token is available
    const headers = new Headers();
    headers.append('Accept', 'application/vnd.github.v3+json');
    
    if (accessToken) {
      console.log('Using GitHub authentication');
      headers.append('Authorization', `token ${accessToken}`);
    }

    // Make the authenticated request
    const response = await fetch(apiUrl, { headers });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`GitHub API error (${response.status}):`, errorText);
      throw new Error(`GitHub API error: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log(`Fetched ${data.length} items from GitHub`);
    
    // Process and return the content
    const processedData = data.map(item => {
      const isFolder = item.type === 'dir';
      const fileExtension = !isFolder ? item.name.split('.').pop().toLowerCase() : null;
      
      // Create jsDelivr URL for the file
      const itemPath = item.path.replace(`${basePath}/`, '');
      const jsdelivrUrl = getGitHubCdnUrl(itemPath);
      
      // Determine media type
      let mediaType = 'unknown';
      if (isFolder) {
        mediaType = 'folder';
      } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
        mediaType = 'image';
      } else if (['mp4', 'webm', 'mov'].includes(fileExtension)) {
        mediaType = 'video';
      }

      return {
        name: item.name,
        path: item.path,
        isFolder,
        fileExtension,
        url: jsdelivrUrl,
        mediaType,
        githubUrl: item.html_url,
        rawUrl: item.download_url // Direct raw content URL
      };
    });
    
    return processedData;
  } catch (error) {
    console.error('Error fetching GitHub content:', error);
    
    // Better error handling for rate limits
    if (error.message && error.message.includes('rate limit')) {
      console.error('GitHub API rate limit exceeded. Consider adding authentication.');
    }
    
    return [];
  }
};

/**
 * Fetches all categories (folders) from the GitHub repository
 * @returns {Promise<Array>} - Array of category objects
 */
export const fetchGitHubCategories = async () => {
  try {
    console.log('Fetching GitHub categories...');
    const content = await fetchGitHubContent();
    
    const categories = content
      .filter(item => {
        // Skip "photo", "works", and "clients" folders
        if (item.isFolder && (
          item.name.toLowerCase() === 'photo' || 
          item.name.toLowerCase() === 'works' ||
          item.name.toLowerCase() === 'clients'
        )) {
          return false;
        }
        return item.isFolder;
      })
      .map(folder => ({
        id: folder.name,
        title: formatCategoryName(folder.name),
        path: folder.path.replace(`${GITHUB_CONFIG.basePath}/`, '')
      }));
    
    console.log(`Found ${categories.length} categories:`, categories);
    return categories;
  } catch (error) {
    console.error('Error fetching GitHub categories:', error);
    return [];
  }
};

/**
 * Fetches media items for a specific category
 * @param {string} category - Category name
 * @returns {Promise<Array>} - Array of media items
 */
export const fetchCategoryMedia = async (category) => {
  try {
    console.log(`Fetching media for category: ${category}`);
    const content = await fetchGitHubContent(category);
    
    const mediaItems = content
      .filter(item => !item.isFolder && item.mediaType !== 'unknown')
      .map(item => ({
        id: item.name,
        title: formatFileName(item.name),
        bannerImage: item.url,
        rawUrl: item.rawUrl,
        mediaType: item.mediaType
      }));
    
    console.log(`Found ${mediaItems.length} media items for category ${category}:`, mediaItems);
    return mediaItems;
  } catch (error) {
    console.error(`Error fetching media for category ${category}:`, error);
    return [];
  }
};

/**
 * Formats a category name for display
 * @param {string} name - Raw category name
 * @returns {string} - Formatted category name
 */
const formatCategoryName = (name) => {
  // Replace underscores and hyphens with spaces
  return name.replace(/[_-]/g, ' ')
    // Split by uppercase letters and join with spaces
    .replace(/([A-Z])/g, ' $1')
    // Trim extra spaces and capitalize first letter of each word
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Formats a file name for display by removing extension and formatting
 * @param {string} name - Raw file name
 * @returns {string} - Formatted file name
 */
const formatFileName = (name) => {
  // Remove file extension
  const nameWithoutExt = name.replace(/\.[^/.]+$/, "");
  // Replace underscores and hyphens with spaces
  return nameWithoutExt.replace(/[_-]/g, ' ')
    // Split by uppercase letters and join with spaces
    .replace(/([A-Z])/g, ' $1')
    // Trim extra spaces and capitalize first letter of each word
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Fallback function to use local images if GitHub API fails
 * @param {string} category - Category name (works, branding, etc.)
 * @param {string} filename - Image filename
 * @returns {string} - URL to the image
 */
export const getImageUrl = (category, filename) => {
  try {
    // Try to use GitHub CDN
    return getGitHubCdnUrl(`${category}/${filename}`);
  } catch (error) {
    // Fallback to local image path
    console.warn('Falling back to local image path:', error);
    return `/images/${category}/${filename}`;
  }
};

/**
 * Test function to check if GitHub API is working
 */
export const testGitHubApi = async () => {
  try {
    console.log('Testing GitHub API...');
    const categories = await fetchGitHubCategories();
    console.log('Categories:', categories);
    
    if (categories.length > 0) {
      const firstCategory = categories[0];
      console.log(`Testing media fetch for category: ${firstCategory.id}`);
      const media = await fetchCategoryMedia(firstCategory.id);
      console.log('Media items:', media);
      
      if (media.length > 0) {
        console.log('First media item:', media[0]);
        console.log('Image URL:', media[0].bannerImage);
        console.log('Raw URL:', media[0].rawUrl);
      }
    }
    
    return 'GitHub API test completed successfully';
  } catch (error) {
    console.error('GitHub API test failed:', error);
    return `GitHub API test failed: ${error.message}`;
  }
};

export default {
  getGitHubCdnUrl,
  fetchGitHubContent,
  fetchGitHubCategories,
  fetchCategoryMedia,
  getImageUrl,
  testGitHubApi
}; 